/**
 * Primary section "Betriebskostenabrechnungen" on the OWNER unit contract
 * editor of WEG properties (EHV-1031).
 *
 * Owners of a WEG can commission the Hausverwaltung to prepare a
 * Betriebskostenabrechnung on their behalf. This section captures, per year,
 * whether the BKA was commissioned ("beauftragt") and whether it is done
 * ("fertig"). State is stored per unit contract in the generic ImpowerX value
 * table via `GET|POST /api/impowerX/unit-contracts/bka` and saved immediately
 * on every checkbox toggle (contract page convention, no Speichern hook).
 *
 * Accordion/numbering behavior matches unitContractNotes.js.
 * @param {{ unitContractId: number | null, propertyHrId: string | null }} ctx
 */
const injectUnitContractBka = (ctx) => {
  const SECTION_ID = 'UnitContractBka'
  const BKA_YEARS = ['2025', '2026']

  const unitContractId = ctx?.unitContractId ?? null
  const propertyHrId = ctx?.propertyHrId || ''
  if (unitContractId == null) return
  // WEG (HOA) properties only.
  if (!propertyHrId.startsWith('WEG')) return

  // Owner contracts only — tenants never commission a BKA for the owner.
  const ownerEditor = document.querySelector('.OwnerContractEditor')
  if (!ownerEditor) return

  const existing = document.getElementById(SECTION_ID)
  if (existing) {
    if (existing.dataset.unitContractId === String(unitContractId)) return
    existing.remove()
  }

  const pageContent =
    ownerEditor.querySelector('.PageContent[data-testid="page-content-undefined"]') ||
    ownerEditor.querySelector('.PageContent')
  if (!pageContent) return

  const pageUsesSectionNumbers = !!pageContent.querySelector('.section-number-wrapper')
  const sectionNumber =
    pageContent.querySelectorAll(':scope > .PrimarySectionWrapper').length + 1

  const apiRoot = typeof baseApiUrl !== 'undefined' && baseApiUrl ? baseApiUrl : ''

  const authToken = () => {
    try {
      const raw = localStorage.getItem('auth')
      return raw ? JSON.parse(raw).token : null
    } catch {
      return null
    }
  }

  const root = document.createElement('div')
  root.className = 'PrimarySectionWrapper UnitContractBka'
  root.id = SECTION_ID
  root.dataset.unitContractId = String(unitContractId)
  root.setAttribute('data-testid', 'lux-unit-contract-bka')

  const wrapperContent = document.createElement('div')
  wrapperContent.className = 'section-wrapper-content'

  const header = document.createElement('div')
  header.className = 'section-header'
  header.setAttribute('role', 'button')
  header.setAttribute('tabindex', '0')

  const titleWrapper = document.createElement('div')
  titleWrapper.className = 'section-title-wrapper'

  const titleEl = document.createElement('div')
  titleEl.className = 'section-title'
  titleEl.textContent = 'Betriebskostenabrechnungen'

  if (pageUsesSectionNumbers) {
    const numberWrap = document.createElement('div')
    numberWrap.className = 'section-number-wrapper'
    const numberEl = document.createElement('div')
    numberEl.className = 'section-number'
    numberEl.textContent = String(sectionNumber)
    numberWrap.appendChild(numberEl)
    titleWrapper.appendChild(numberWrap)
  }
  titleWrapper.appendChild(titleEl)
  header.appendChild(titleWrapper)

  const sectionBody = document.createElement('div')
  sectionBody.className = 'section-content section-content-with-padding'
  sectionBody.style.display = 'none'

  const checkboxGroupHtml = (groupKey, label) => `
    <div class="lux-uc-bka-group" style="min-width: 25rem;">
      <div class="ant-row ant-form-item" style="row-gap: 0px;">
        <div class="ant-col ant-form-item-label">
          <label title=""><span>${label}</span></label>
        </div>
        <div class="ant-col ant-form-item-control">
          <div class="ant-form-item-control-input">
            <div class="ant-form-item-control-input-content">
              <div class="ant-checkbox-group lux-uc-bka-checkbox-group" data-bka-group="${groupKey}">
                ${BKA_YEARS.map(
                  (year) => `<label class="ant-checkbox-wrapper ant-checkbox-wrapper-in-form-item ant-checkbox-group-item">
                    <span class="ant-checkbox">
                      <input type="checkbox" class="ant-checkbox-input" value="${year}" disabled />
                      <span class="ant-checkbox-inner"></span>
                    </span>
                    <span style="font-size: 16px;">${year}</span>
                  </label>`
                ).join('')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `

  sectionBody.innerHTML = `
    <div class="lux-uc-bka-inner">
      <div class="lux-uc-bka-loading" style="padding: 8px 0; color: rgba(0,0,0,0.45);">
        Wird geladen…
      </div>
      <div class="lux-uc-bka-content" style="display: none;">
        <div style="display: flex; flex-wrap: wrap; gap: 24px;">
          ${checkboxGroupHtml('beauftragt', 'Betriebskostenabrechnung beauftragt')}
          ${checkboxGroupHtml('fertig', 'Betriebskostenabrechnung fertig')}
        </div>
        <div class="lux-uc-bka-status" style="min-height: 20px; margin-top: 8px; font-size: 12px; color: rgba(0,0,0,0.45);"></div>
      </div>
    </div>
  `

  const loadingEl = sectionBody.querySelector('.lux-uc-bka-loading')
  const contentEl = sectionBody.querySelector('.lux-uc-bka-content')
  const statusEl = sectionBody.querySelector('.lux-uc-bka-status')

  const groupInputs = (groupKey) =>
    Array.from(
      sectionBody.querySelectorAll(
        `.lux-uc-bka-checkbox-group[data-bka-group="${groupKey}"] input[type="checkbox"]`
      )
    )

  const setCheckboxChecked = (input, checked) => {
    input.checked = checked
    const box = input.closest('.ant-checkbox')
    if (box) box.classList.toggle('ant-checkbox-checked', checked)
  }

  const selectedYears = (groupKey) =>
    groupInputs(groupKey)
      .filter((input) => input.checked)
      .map((input) => input.value)

  const applyState = (state) => {
    for (const groupKey of ['beauftragt', 'fertig']) {
      const years = Array.isArray(state?.[groupKey]) ? state[groupKey] : []
      groupInputs(groupKey).forEach((input) => {
        setCheckboxChecked(input, years.includes(input.value))
        input.disabled = false
      })
    }
  }

  const setStatus = (text, isError) => {
    statusEl.textContent = text || ''
    statusEl.style.color = isError ? '#ff4d4f' : 'rgba(0,0,0,0.45)'
  }

  async function loadState() {
    const token = authToken()
    if (!token) throw new Error('no auth token')
    const res = await fetch(
      `${apiRoot}/api/impowerX/unit-contracts/bka?unitContractId=${encodeURIComponent(unitContractId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    if (!res.ok) throw new Error('load failed')
    return res.json()
  }

  let saveInFlight = false
  let savePending = false
  let pendingChangedInput = null

  async function saveState(changedInput) {
    const token = authToken()
    if (!token) {
      alert('Bitte erneut anmelden.')
      setCheckboxChecked(changedInput, !changedInput.checked)
      return
    }
    if (saveInFlight) {
      // A toggle happened mid-save: persist the full state again afterwards.
      savePending = true
      pendingChangedInput = changedInput
      return
    }
    saveInFlight = true
    setStatus('Speichert…', false)
    try {
      const res = await fetch(`${apiRoot}/api/impowerX/unit-contracts/bka`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          unitContractId,
          beauftragt: selectedYears('beauftragt'),
          fertig: selectedYears('fertig'),
        }),
      })
      if (!res.ok) throw new Error(`save failed (${res.status})`)
      const data = await res.json()
      // Don't overwrite the UI with a stale response if more toggles queued up.
      if (!savePending) applyState(data)
      setStatus(`Gespeichert (${new Date().toLocaleTimeString()})`, false)
    } catch (e) {
      console.error(e)
      if (!savePending) {
        setCheckboxChecked(changedInput, !changedInput.checked)
      }
      setStatus('Speichern fehlgeschlagen. Bitte erneut versuchen.', true)
    } finally {
      saveInFlight = false
      if (savePending) {
        const queuedChangedInput = pendingChangedInput
        savePending = false
        pendingChangedInput = null
        saveState(queuedChangedInput)
      }
    }
  }

  sectionBody.addEventListener('change', (event) => {
    const input = event.target.closest('.lux-uc-bka-checkbox-group input[type="checkbox"]')
    if (!input) return
    setCheckboxChecked(input, input.checked)
    saveState(input)
  })

  let isOpen = false
  let didLoad = false

  const applyOpenState = (open) => {
    isOpen = open
    if (open) {
      wrapperContent.classList.add('section-wrapper-open')
      sectionBody.style.display = ''
      if (!didLoad) {
        didLoad = true
        loadState()
          .then((state) => {
            applyState(state)
            loadingEl.style.display = 'none'
            contentEl.style.display = ''
          })
          .catch((e) => {
            console.error(e)
            didLoad = false
            loadingEl.textContent = 'Daten konnten nicht geladen werden.'
          })
      }
    } else {
      wrapperContent.classList.remove('section-wrapper-open')
      sectionBody.style.display = 'none'
    }
  }

  const toggle = () => applyOpenState(!isOpen)

  // Close when another section header is clicked (same accordion behavior as notes).
  const onOtherSectionHeaderClick = (e) => {
    const headerEl = e.target.closest('.section-header')
    if (!headerEl) return
    const clickedWrapper = headerEl.closest('.section-wrapper-content')
    if (!clickedWrapper || clickedWrapper === wrapperContent) return
    if (isOpen) applyOpenState(false)
  }
  pageContent.addEventListener('click', onOtherSectionHeaderClick, true)

  header.addEventListener('click', (e) => {
    e.preventDefault()
    toggle()
  })
  header.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggle()
    }
  })

  wrapperContent.appendChild(header)
  wrapperContent.appendChild(sectionBody)
  root.appendChild(wrapperContent)
  pageContent.appendChild(root)
}
